<script setup>
import { reactive, computed, ref, onMounted, watch } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { email, required } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import SearchableDropdown from './SearchableDropdown.vue';
import CrmAPI from 'dashboard/api/integrations/crm';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  contact: {
    type: Object,
    default: () => ({}),
  },
  integrationId: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['submit', 'close']);

const mustBeTrue = value => value === true;
const optionalEmail = value => {
  if (!value) return true; // If no value, it's valid
  return email.$validator(value); // Validate as email if value exists
};

const CrmApiIntegration = new CrmAPI(props.integrationId);
const isSubmitting = ref(false);
const currentStep = ref(1);
const showErrors = ref(false);
const inputStyles = { borderRadius: '12px', fontSize: '14px' };

const dynamicFields = ref([]);
const dynamicForm = reactive({});
const products = ref([]);
const funnels = ref([]);
const stages = ref([]);
const users = ref([]);

const contactId = ref(null);
const wasContactFound = ref(false);
const manualContactEntry = ref(false);
const backupContact = ref({});

const formState = reactive({
  titulo: '',
  nomeContato: '',
  email: '',
  whatsapp: props.contact?.phone || '',
  consentimento: false,
  produtosSelecionados: [],
  funil: '',
  etapa: '',
  responsavel: '',
  avaliacao: 0,
  atribuicao: 'pessoa',
});

// Helper function to check if a dynamic field is required
const isFieldRequired = field => {
  // If field has required = true, it's always required
  if (field.required === false) {
    return false;
  }
  const currentStageId = formState.etapa;
  // If field has require_rules, check based on deal_stage_id
  if (field.required_rules && Array.isArray(field.required_rules)) {
    // Check if any rule matches the current stage
    return field.required_rules.some(rule => {
      // If rule has always = true, field is always required
      if (rule.always === true) {
        return true;
      }

      // If rule has deal_stage_id, check if it matches current stage
      if (rule.value && currentStageId) {
        return rule.value === currentStageId;
      }

      return false;
    });
  }

  // Default to not required if no rules apply
  return false;
};

const validations = computed(() => {
  const rules = {
    titulo: { required },
    nomeContato: { required },
    whatsapp: { required },
    consentimento: { mustBeTrue },
    email: { optionalEmail },
    funil: { required },
    etapa: { required },
    responsavel: { required },
  };

  // Add dynamic field validations based on require_rules
  dynamicFields.value.forEach(field => {
    if (isFieldRequired(field)) {
      rules[field.id] = { required };
    }
  });

  return rules;
});

const validationData = computed(() => ({ ...formState, ...dynamicForm }));
const v$ = useVuelidate(validations, validationData);

const getErrorMessage = (field, fieldValidation) => {
  if (!fieldValidation || !fieldValidation.$error || !showErrors.value)
    return '';

  // For email field, always show email-specific error if there's any validation error
  if (field === 'email') {
    return 'Por favor, insira um email válido';
  }

  if (field === 'consentimento')
    return 'É necessário confirmar o consentimento';

  return 'Valor inválido';
};

const getDynamicFieldErrorMessage = fieldId => {
  const fieldValidation = v$.value?.[fieldId];
  if (!fieldValidation || !fieldValidation.$error || !showErrors.value)
    return '';

  const field = dynamicFields.value.find(f => f.id === fieldId);
  if (!field || !isFieldRequired(field)) return '';

  // Check if field has a valid value based on its type
  const fieldValue = dynamicForm[fieldId];
  if (field.type === 'multiple_choice') {
    if (!Array.isArray(fieldValue) || fieldValue.length === 0) {
      return 'Este campo é obrigatório';
    }
  } else if (
    fieldValue === null ||
    fieldValue === undefined ||
    fieldValue === ''
  ) {
    return 'Este campo é obrigatório';
  }

  return '';
};

// Computed error messages
const tituloError = computed(() => getErrorMessage('titulo', v$.value?.titulo));
const nomeContatoError = computed(() =>
  getErrorMessage('nomeContato', v$.value?.nomeContato)
);
const emailError = computed(() => getErrorMessage('email', v$.value?.email));
const whatsappError = computed(() =>
  getErrorMessage('whatsapp', v$.value?.whatsapp)
);
const consentimentoError = computed(() =>
  getErrorMessage('consentimento', v$.value?.consentimento)
);
const funilError = computed(() => getErrorMessage('funil', v$.value?.funil));
const etapaError = computed(() => getErrorMessage('etapa', v$.value?.etapa));
const responsavelError = computed(() =>
  getErrorMessage('responsavel', v$.value?.responsavel)
);

const produtosError = computed(() => {
  return formState.produtosSelecionados.length === 0 && showErrors.value
    ? 'Selecione pelo menos um produto'
    : '';
});

// Change handlers
const handleFunilChange = async item => {
  formState.funil = item.id;
  v$.value.funil?.$touch();
  const selectedFunnel = funnels.value.find(funnel => funnel.id === item.id);
  if (selectedFunnel && selectedFunnel.deal_stages) {
    stages.value = selectedFunnel.deal_stages.map(stage => ({
      id: stage.id,
      name: stage.name,
    }));
  } else {
    stages.value = [];
  }
  formState.etapa = '';
};

const handleEtapaChange = item => {
  formState.etapa = item.id;
  v$.value.etapa?.$touch();
};

const handleResponsavelChange = item => {
  formState.responsavel = item.id;
  v$.value.responsavel?.$touch();
};

const handleProductChange = item => {
  const productIndex = formState.produtosSelecionados.findIndex(
    p => p.id === item.id
  );
  if (productIndex > -1) {
    formState.produtosSelecionados.splice(productIndex, 1);
  } else {
    formState.produtosSelecionados.push({
      id: item.id,
      name: item.name,
      price: item.price,
      amount: 1, // Default amount
      customPrice: item.price, // Allow custom price editing
    });
  }
};

const updateProductQuantity = (productId, amount) => {
  const productIndex = formState.produtosSelecionados.findIndex(
    p => p.id === productId
  );
  if (productIndex > -1 && amount > 0) {
    formState.produtosSelecionados[productIndex].amount = parseInt(amount, 10);
  }
};

const updateProductPrice = (productId, price) => {
  const productIndex = formState.produtosSelecionados.findIndex(
    p => p.id === productId
  );
  if (productIndex > -1 && price >= 0) {
    formState.produtosSelecionados[productIndex].customPrice =
      parseFloat(price) || 0;
  }
};

const handleDynamicFieldChange = (item, fieldId) => {
  dynamicForm[fieldId] = item.id;
  v$.value[fieldId]?.$touch();
};

const handleMultipleChoiceChange = (item, fieldId) => {
  const currentSelection = dynamicForm[fieldId] || [];
  const itemIndex = currentSelection.indexOf(item.id);

  if (itemIndex > -1) {
    // Remove if already selected
    dynamicForm[fieldId] = currentSelection.filter(id => id !== item.id);
  } else {
    // Add to selection
    dynamicForm[fieldId] = [...currentSelection, item.id];
  }

  v$.value[fieldId]?.$touch();
};

const handleDynamicFieldInput = fieldId => {
  if (v$.value?.[fieldId]) {
    v$.value[fieldId].$touch();
  }
};

const loadDynamicFields = async () => {
  const response = await CrmApiIntegration.getCustomfields();
  const json = await response.data;
  dynamicFields.value = json;

  json.forEach(field => {
    if (field.type === 'option') {
      // Single choice field
      dynamicForm[field.id] = null;
    } else if (field.type === 'multiple_choice') {
      // Multiple choice field - array of selected indices
      dynamicForm[field.id] = [];
    } else if (field.type === 'text' || field.type === 'date') {
      dynamicForm[field.id] = '';
    } else {
      dynamicForm[field.id] = null;
    }
  });
};

const loadAllProducts = async () => {
  try {
    let allProducts = [];
    let currentPage = 1;
    let hasMore = true;
    const limit = 200;

    while (hasMore) {
      // eslint-disable-next-line no-await-in-loop
      const response = await CrmApiIntegration.getProducts(currentPage, limit);
      // eslint-disable-next-line no-await-in-loop
      const data = await response.data;

      allProducts = [...allProducts, ...data.products];
      hasMore = data.has_more;
      currentPage += 1;

      if (currentPage > 100) {
        // eslint-disable-next-line no-console
        console.warn(
          'Stopped fetching products after 100 pages to prevent infinite loop'
        );
        break;
      }
    }

    products.value = allProducts.map(product => ({
      id: product.id,
      name: product.name,
      price: product.base_price,
    }));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error loading products:', error);
    useAlert('Erro ao carregar produtos');
  }
};

const loadFunnels = async () => {
  try {
    const response = await CrmApiIntegration.getFunnels();
    const data = await response.data;
    funnels.value = data.map(funnel => ({
      id: funnel.id,
      name: funnel.name,
      deal_stages: funnel.deal_stages,
    }));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error loading funnels:', error);
    useAlert('Erro ao carregar funis');
  }
};

const loadUsers = async () => {
  try {
    const response = await CrmApiIntegration.getUsers();
    const data = await response.data?.users;
    users.value = data.map(user => ({
      id: user.id,
      name: user.name,
    }));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error loading users:', error);
    useAlert('Erro ao carregar usuários');
  }
};

const applyContactData = contact => {
  if (!contact) return;
  backupContact.value = { ...contact };
  formState.nomeContato = contact.name || '';
  formState.email = contact.email || '';
  formState.whatsapp = contact.phone || props.contact?.phone || '';
};

const undoManualEdit = () => {
  manualContactEntry.value = false;
  applyContactData(backupContact.value);
};

const fetchContactByPhone = async () => {
  try {
    if (!props.contact?.phone) {
      wasContactFound.value = false;
      applyContactData(props.contact);
      return;
    }

    const response = await CrmApiIntegration.getContact(
      encodeURIComponent(props.contact?.phone)
    );
    const contact = (await response.data?.contacts?.[0]) || null;

    if (contact && contact.id) {
      contactId.value = contact.id;
      wasContactFound.value = true;
      applyContactData(contact);
    } else {
      wasContactFound.value = false;
      applyContactData(props.contact);
    }
  } catch (error) {
    wasContactFound.value = false;
    applyContactData(props.contact);
    useAlert('Erro ao buscar contato');
  }
};

onMounted(() => {
  fetchContactByPhone();
  loadDynamicFields();
  loadAllProducts();
  loadFunnels();
  loadUsers();
});

const validateCurrentStep = async () => {
  v$.value?.$touch();

  if (currentStep.value === 1) {
    const {
      titulo,
      nomeContato,
      whatsapp,
      email: emailField,
      consentimento,
    } = v$.value;
    return !(
      titulo.$invalid ||
      nomeContato.$invalid ||
      whatsapp.$invalid ||
      emailField.$invalid ||
      consentimento.$invalid
    );
  }

  if (currentStep.value === 2) {
    const { funil, etapa, responsavel } = v$.value;
    const dynamicInvalid = dynamicFields.value.some(field => {
      if (!isFieldRequired(field)) return false;

      const fieldValue = dynamicForm[field.id];

      // Check based on field type
      if (field.type === 'multiple_choice') {
        return !Array.isArray(fieldValue) || fieldValue.length === 0;
      }

      return (
        fieldValue === null || fieldValue === undefined || fieldValue === ''
      );
    });
    const productsInvalid = formState.produtosSelecionados.length === 0;
    return !(
      funil.$invalid ||
      etapa.$invalid ||
      responsavel.$invalid ||
      dynamicInvalid ||
      productsInvalid
    );
  }

  return true;
};

const nextStep = async () => {
  showErrors.value = true;
  const isValid = await validateCurrentStep();
  if (isValid) {
    currentStep.value += 1;
  }
};

const previousStep = () => {
  currentStep.value -= 1;
};

const submitFinal = async () => {
  v$.value?.$touch();
  if (v$.value?.$invalid) {
    showErrors.value = true;
    return;
  }

  // Build the deal payload according to RD Station API structure
  const dealPayload = {
    conversation_id: props.conversationId,
    contacts: [],
    deal: {
      name: `#${props.conversationId} ${formState.titulo}`,
      deal_stage_id: formState.etapa,
      rating: formState.avaliacao,
      user_id: formState.responsavel,
      deal_custom_fields: [],
    },
    deal_products: [],
    distribution_settings: {
      owner: {
        id: formState.responsavel,
        type: formState.atribuicao === 'pessoa' ? 'user' : 'team',
      },
    },
  };

  // Add contact information
  if (wasContactFound.value && !manualContactEntry.value && contactId.value) {
    // Use existing contact
    dealPayload.set_contacts = [{ id: contactId.value }];
  } else {
    // Create new contact
    const contactData = {
      name: formState.nomeContato,
      phones: [{ phone: formState.whatsapp, type: 'cellphone' }],
    };

    if (formState.email) {
      contactData.emails = [{ email: formState.email }];
    }

    // Add consent information
    if (formState.consentimento) {
      contactData.legal_bases = [
        {
          category: 'communications',
          status: 'granted',
          type: 'consent',
        },
      ];
    }

    dealPayload.contacts = [contactData];
  }

  // Add selected products to deal_products
  if (formState.produtosSelecionados.length > 0) {
    dealPayload.deal_products = formState.produtosSelecionados.map(product => ({
      id: product.id,
      name: product.name,
      base_price: parseFloat(product.price), // Original price
      price: parseFloat(product.customPrice), // Custom price
      amount: product.amount,
      total: parseFloat(product.customPrice * product.amount),
      discount_type: 'value',
      recurrence: 'spare',
    }));
  }

  // Add dynamic custom fields
  dynamicFields.value.forEach(field => {
    const fieldValue = dynamicForm[field.id];
    let hasValue = false;
    let finalValue;

    // Check if field has a value based on its type
    if (field.type === 'option') {
      hasValue =
        fieldValue !== null && fieldValue !== undefined && fieldValue !== '';
      finalValue = hasValue ? field.opts[fieldValue] : null;
    } else if (field.type === 'multiple_choice') {
      hasValue = Array.isArray(fieldValue) && fieldValue.length > 0;
      finalValue = hasValue ? fieldValue.map(index => field.opts[index]) : null;
    } else if (field.type === 'text' || field.type === 'date') {
      hasValue =
        fieldValue !== null && fieldValue !== undefined && fieldValue !== '';
      finalValue = hasValue ? fieldValue : null;
    }

    if (hasValue && finalValue) {
      dealPayload.deal.deal_custom_fields.push({
        custom_field_id: field.id,
        value: finalValue,
      });
    }
  });

  try {
    isSubmitting.value = true;
    const response = await CrmApiIntegration.createDeal(dealPayload);
    useAlert('Negociação criada com sucesso!');
    emit('submit', response.data);
    emit('close');
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error creating deal:', error);
    useAlert('Erro ao criar negociação');
  } finally {
    isSubmitting.value = false;
  }
};

// Watch for stage changes to re-validate dynamic fields
watch(
  () => formState.etapa,
  () => {
    // Re-validate all dynamic fields when stage changes
    dynamicFields.value.forEach(field => {
      const fieldValidation = v$.value?.[field.id];
      if (fieldValidation) {
        fieldValidation.$touch();
      }
    });
  }
);
</script>

<template>
  <div class="space-y-4">
    <!-- Step 1: Contact Information -->
    <template v-if="currentStep === 1">
      <woot-input
        v-model="formState.titulo"
        :styles="inputStyles"
        label="Título da Negociação"
        placeholder="Digite o título da negociação"
        :error="tituloError"
      />

      <div class="relative">
        <woot-input
          v-model="formState.nomeContato"
          :styles="inputStyles"
          label="Nome do Contato"
          placeholder="Nome do contato"
          :disabled="!manualContactEntry && wasContactFound"
          :error="nomeContatoError"
        />
        <button
          v-if="wasContactFound && !manualContactEntry"
          class="edit-contact-btn"
          @click="manualContactEntry = true"
        >
          Editar
        </button>
      </div>

      <woot-input
        v-model="formState.email"
        :styles="inputStyles"
        label="Email"
        placeholder="email@exemplo.com"
        :disabled="!manualContactEntry && wasContactFound"
        :error="emailError"
      />

      <woot-input
        v-model="formState.whatsapp"
        :styles="inputStyles"
        label="WhatsApp"
        placeholder="Número do WhatsApp"
        disabled
        :error="whatsappError"
      />

      <button
        v-if="manualContactEntry"
        class="undo-contact-btn"
        @click="undoManualEdit"
      >
        Cancelar edição
      </button>

      <label class="consent-label">
        <input v-model="formState.consentimento" type="checkbox" />
        O contato deu consentimento para contato
      </label>
      <span v-if="consentimentoError" class="error-message">
        {{ consentimentoError }}
      </span>

      <div class="flex justify-end gap-2">
        <woot-button @click="nextStep">Próximo</woot-button>
      </div>
    </template>

    <!-- Step 2: Deal Information -->
    <template v-if="currentStep === 2">
      <!-- Products Selection -->
      <div class="mb-4">
        <label class="block text-sm font-medium text-gray-700 mb-2">
          Produtos
        </label>
        <div v-if="formState.produtosSelecionados.length > 0" class="mb-4">
          <div
            v-for="product in formState.produtosSelecionados"
            :key="product.id"
            class="bg-gray-50 p-4 rounded-lg mb-3 border"
          >
            <div class="flex items-start justify-between">
              <div class="flex-1">
                <h4 class="font-medium text-gray-900 mb-2">
                  {{ product.name }}
                </h4>
                <div class="grid grid-cols-2 gap-4">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                      Quantidade
                    </label>
                    <input
                      :value="product.amount"
                      type="number"
                      min="1"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      @input="
                        updateProductQuantity(product.id, $event.target.value)
                      "
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                      Preço (R$)
                    </label>
                    <input
                      :value="product.customPrice"
                      type="number"
                      min="0"
                      step="0.01"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      @input="
                        updateProductPrice(product.id, $event.target.value)
                      "
                    />
                  </div>
                </div>
                <div class="mt-2 text-sm text-gray-600">
                  Total: R$
                  {{
                    (Number(product.customPrice) * product.amount).toFixed(2)
                  }}
                </div>
              </div>
              <button
                class="ml-4 text-red-600 hover:text-red-800 text-sm"
                @click="handleProductChange(product)"
              >
                Remover
              </button>
            </div>
          </div>
        </div>
        <SearchableDropdown
          :value="null"
          label="Adicionar Produto"
          type="produtos"
          placeholder="Selecione um produto"
          :items="products"
          :error="produtosError"
          @change="handleProductChange"
        />
      </div>

      <SearchableDropdown
        :value="formState.funil"
        label="Funil"
        type="funil"
        placeholder="Selecione um funil"
        :items="funnels"
        :error="funilError"
        @change="handleFunilChange"
      />

      <SearchableDropdown
        :value="formState.etapa"
        label="Etapa"
        type="etapa"
        placeholder="Selecione uma etapa"
        :items="stages"
        :error="etapaError"
        @change="handleEtapaChange"
      />

      <SearchableDropdown
        :value="formState.responsavel"
        label="Responsável"
        type="responsavel"
        placeholder="Selecione um responsável"
        :items="users"
        :error="responsavelError"
        @change="handleResponsavelChange"
      />

      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">
          Avaliação da Negociação
        </label>
        <div class="star-rating">
          <span
            v-for="i in 5"
            :key="i"
            class="star"
            :class="{ active: formState.avaliacao >= i }"
            @click="formState.avaliacao = i"
          >
            {{ formState.avaliacao >= i ? '★' : '☆' }}
          </span>
        </div>
      </div>

      <!-- Dynamic Fields -->
      <div v-for="field in dynamicFields" :key="field.id">
        <!-- Single choice dropdown (option) -->
        <SearchableDropdown
          v-if="field.type === 'option'"
          :value="dynamicForm[field.id]"
          :label="`${field.label}${isFieldRequired(field) ? ' *' : ''}`"
          :type="field.id"
          :placeholder="`Selecione ${field.label.toLowerCase()}`"
          :items="field.opts.map((opt, i) => ({ id: i, name: opt }))"
          :error="getDynamicFieldErrorMessage(field.id)"
          @change="item => handleDynamicFieldChange(item, field.id)"
        />

        <!-- Multiple choice dropdown -->
        <div v-else-if="field.type === 'multiple_choice'" class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            {{ field.label }}{{ isFieldRequired(field) ? ' *' : '' }}
          </label>
          <div v-if="(dynamicForm[field.id] || []).length > 0" class="mb-2">
            <span
              v-for="selectedIndex in dynamicForm[field.id]"
              :key="selectedIndex"
              class="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-sm rounded-full mr-2 mb-2"
            >
              {{ field.opts[selectedIndex] }}
              <button
                class="ml-2 text-blue-600 hover:text-blue-800"
                @click="
                  handleMultipleChoiceChange({ id: selectedIndex }, field.id)
                "
              >
                ×
              </button>
            </span>
          </div>
          <SearchableDropdown
            :value="null"
            :label="`Adicionar ${field.label}`"
            :type="field.id"
            :placeholder="`Selecione ${field.label.toLowerCase()}`"
            :items="field.opts.map((opt, i) => ({ id: i, name: opt }))"
            :error="getDynamicFieldErrorMessage(field.id)"
            @change="item => handleMultipleChoiceChange(item, field.id)"
          />
        </div>

        <woot-input
          v-else-if="field.type === 'text'"
          v-model="dynamicForm[field.id]"
          :label="`${field.label}${isFieldRequired(field) ? ' *' : ''}`"
          :placeholder="`Digite ${field.label.toLowerCase()}`"
          :styles="inputStyles"
          :error="getDynamicFieldErrorMessage(field.id)"
          @input="() => handleDynamicFieldInput(field.id)"
        />

        <woot-input
          v-else-if="field.type === 'date'"
          v-model="dynamicForm[field.id]"
          :label="`${field.label}${isFieldRequired(field) ? ' *' : ''}`"
          :placeholder="`Selecione ${field.label.toLowerCase()}`"
          type="date"
          :styles="inputStyles"
          :error="getDynamicFieldErrorMessage(field.id)"
          @input="() => handleDynamicFieldInput(field.id)"
        />
      </div>

      <div class="flex justify-between mt-4">
        <woot-button @click="previousStep">Anterior</woot-button>
        <woot-button @click="nextStep">Próximo</woot-button>
      </div>
    </template>

    <!-- Step 3: Review -->
    <template v-if="currentStep === 3">
      <div class="review-section">
        <h3 class="text-lg font-semibold mb-4">Revisão dos Dados</h3>

        <div class="review-item">
          <label>Título:</label>
          <span>{{ formState.titulo }}</span>
        </div>

        <div class="review-item">
          <label>Contato:</label>
          <span>{{ formState.nomeContato }}</span>
        </div>

        <div v-if="formState.email" class="review-item">
          <label>Email:</label>
          <span>{{ formState.email }}</span>
        </div>

        <div class="review-item">
          <label>WhatsApp:</label>
          <span>{{ formState.whatsapp }}</span>
        </div>

        <div class="review-item">
          <label>Produtos:</label>
          <div class="text-right">
            <div
              v-for="product in formState.produtosSelecionados"
              :key="product.id"
              class="mb-1"
            >
              {{ product.name }} - Qtd: {{ product.amount }} - R$
              {{ Number(product.customPrice).toFixed(2) }} (Total: R$
              {{ (Number(product.customPrice) * product.amount).toFixed(2) }})
            </div>
          </div>
        </div>

        <div class="review-item">
          <label>Funil:</label>
          <span>{{ funnels.find(f => f.id === formState.funil)?.name }}</span>
        </div>

        <div class="review-item">
          <label>Etapa:</label>
          <span>{{ stages.find(s => s.id === formState.etapa)?.name }}</span>
        </div>

        <div class="review-item">
          <label>Responsável:</label>
          <span>{{
            users.find(u => u.id === formState.responsavel)?.name
          }}</span>
        </div>

        <div class="review-item">
          <label>Avaliação:</label>
          <span>{{ formState.avaliacao }} estrela(s)</span>
        </div>

        <div v-for="field in dynamicFields" :key="field.id" class="review-item">
          <template
            v-if="
              (field.type === 'option' &&
                dynamicForm[field.id] !== null &&
                dynamicForm[field.id] !== undefined) ||
              (field.type === 'multiple_choice' &&
                Array.isArray(dynamicForm[field.id]) &&
                dynamicForm[field.id].length > 0) ||
              (field.type !== 'option' &&
                field.type !== 'multiple_choice' &&
                dynamicForm[field.id] !== null &&
                dynamicForm[field.id] !== undefined &&
                dynamicForm[field.id] !== '')
            "
          >
            <label>{{ field.label }}:</label>
            <span v-if="field.type === 'option'">
              {{ field.opts[dynamicForm[field.id]] }}
            </span>
            <span v-else-if="field.type === 'multiple_choice'">
              {{
                (dynamicForm[field.id] || [])
                  .map(index => field.opts[index])
                  .join(', ')
              }}
            </span>
            <span v-else>
              {{ dynamicForm[field.id] }}
            </span>
          </template>
        </div>
      </div>

      <div class="flex justify-between mt-4">
        <woot-button @click="previousStep">Anterior</woot-button>
        <woot-button :is-loading="isSubmitting" @click.prevent="submitFinal">
          Criar Negociação
        </woot-button>
      </div>
    </template>
  </div>
</template>

<style scoped>
.edit-contact-btn {
  margin-top: 4px;
  font-size: 13px;
  color: #3b82f6;
  text-decoration: underline;
  background: none;
  border: none;
  cursor: pointer;
}
.edit-contact-btn:hover {
  color: #2563eb;
}
.undo-contact-btn {
  margin-top: 4px;
  font-size: 13px;
  color: #ef4444;
  text-decoration: underline;
  background: none;
  border: none;
  cursor: pointer;
}
.undo-contact-btn:hover {
  color: #dc2626;
}

.consent-label {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.error-message {
  display: block;
  color: #ef4444;
  font-size: 12px;
  margin-top: 4px;
}

.star-rating {
  display: flex;
  gap: 4px;
}

.star {
  cursor: pointer;
  font-size: 24px;
  color: #d1d5db;
  transition: color 0.2s;
}

.star.active {
  color: #fbbf24;
}

.star:hover {
  color: #f59e0b;
}

.review-section {
  background: #f9fafb;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 16px;
}

.review-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #e5e7eb;
}

.review-item:last-child {
  border-bottom: none;
}

.review-item label {
  font-weight: 500;
  color: #374151;
  flex-shrink: 0;
  margin-right: 16px;
}

.review-item span {
  color: #6b7280;
  text-align: right;
}

/* Ensure all error messages are styled consistently in red */
:deep(.message) {
  color: #ef4444 !important;
  font-size: 12px;
  margin-top: 4px;
}

/* Style error state for inputs and labels */
:deep(.error) {
  color: #ef4444;
}

:deep(.error input) {
  border-color: #ef4444 !important;
  outline-color: #ef4444 !important;
}

:deep(.error label) {
  color: #ef4444;
}

/* Specific styling for woot-input error messages */
:deep(.input-container .message) {
  color: #ef4444 !important;
  font-size: 12px;
  margin-top: 4px;
  display: block;
}

/* SearchableDropdown error styling */
:deep(.searchable-dropdown .message) {
  color: #ef4444 !important;
  font-size: 12px;
}
</style>
